import React, { forwardRef } from 'react';
import { View, Text, StyleSheet, Image } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';

export interface DuelPlayer {
  username: string;
  avatarUrl?: string;
  score: number;
}

export interface DuelShareCardProps {
  winner: DuelPlayer;
  loser: DuelPlayer;
  exercise: string;
  duelType: string;
  duration: string;
  /** Current user is the winner */
  isCurrentUserWinner?: boolean;
  /** Win/loss record update e.g. "5W - 2L" */
  record?: string;
  /** Force 9:16 story ratio (1080×1920) for Instagram/TikTok */
  storyMode?: boolean;
  /** Unit label for scores (e.g. 'lbs' or 'kg'). Defaults to 'lbs'. */
  unit?: string;
  /** Show watermark for free users */
  showWatermark?: boolean;
}

const DuelShareCard = forwardRef<View, DuelShareCardProps>(
  ({ winner, loser, exercise, duelType, duration, isCurrentUserWinner = true, record, storyMode, unit = 'lbs', showWatermark }, ref) => {
    const formatScore = (s: number) =>
      s >= 1000 ? `${(s / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(s);

    const dateStr = new Date().toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    const headerText = isCurrentUserWinner ? 'DUEL WON' : 'DUEL LOST';

    return (
      <View ref={ref} style={[styles.card, storyMode && styles.cardStory]} collapsable={false}>
        {/* Gold glow at top */}
        <LinearGradient
          colors={[
            isCurrentUserWinner ? 'rgba(184,151,46,0.25)' : 'rgba(100,100,100,0.15)',
            'transparent',
          ]}
          style={styles.topGlow}
        />

        {/* Top accent bar */}
        <View style={[styles.accentBar, !isCurrentUserWinner && { backgroundColor: '#4A4840' }]} />

        <View style={styles.content}>
          {/* Brand */}
          <View style={styles.brandBlock}>
            <Text style={styles.brandText}>DUEL</Text>
            <View style={styles.brandLine} />
          </View>

          {/* Header */}
          <Text style={[styles.headerLabel, !isCurrentUserWinner && { color: '#7A776E' }]}>
            {headerText}
          </Text>
          <Text style={styles.date}>{dateStr}</Text>

          {/* Exercise and meta */}
          <View style={styles.exerciseBlock}>
            <Text style={styles.exercise}>{exercise.toUpperCase()}</Text>
            <Text style={styles.meta}>{duelType} • {duration}</Text>
          </View>

          {/* Battle section */}
          <View style={styles.battleSection}>
            {/* Winner side */}
            <View style={[styles.playerSide, styles.winnerSide]}>
              <Text style={styles.winnerLabel}>👑 WINNER</Text>
              {winner.avatarUrl ? (
                <Image source={{ uri: winner.avatarUrl }} style={styles.avatar} />
              ) : (
                <View style={[styles.avatarPlaceholder, styles.winnerAvatar]}>
                  <Text style={styles.avatarLetter}>
                    {winner.username.charAt(0).toUpperCase()}
                  </Text>
                </View>
              )}
              <Text style={styles.playerName} numberOfLines={1}>
                @{winner.username}
              </Text>
              <Text style={[styles.score, styles.winnerScore]}>{formatScore(winner.score)}</Text>
              <Text style={styles.scoreUnit}>{unit}</Text>
            </View>

            {/* VS separator */}
            <View style={styles.vsContainer}>
              <View style={styles.vsBadge}>
                <Text style={styles.vsText}>VS</Text>
              </View>
            </View>

            {/* Loser side */}
            <View style={[styles.playerSide, styles.loserSide]}>
              <Text style={styles.loserLabel}>DEFEATED</Text>
              {loser.avatarUrl ? (
                <Image source={{ uri: loser.avatarUrl }} style={styles.avatar} />
              ) : (
                <View style={[styles.avatarPlaceholder, styles.loserAvatar]}>
                  <Text style={styles.avatarLetter}>
                    {loser.username.charAt(0).toUpperCase()}
                  </Text>
                </View>
              )}
              <Text style={styles.playerName} numberOfLines={1}>
                @{loser.username}
              </Text>
              <Text style={[styles.score, styles.loserScore]}>{formatScore(loser.score)}</Text>
              <Text style={styles.scoreUnit}>{unit}</Text>
            </View>
          </View>

          {/* Summary */}
          <View style={styles.summaryBlock}>
            <View style={styles.summaryLine} />
            <View style={styles.summaryCenter}>
              <Text style={styles.summaryText}>
                @{winner.username} defeated @{loser.username}
              </Text>
              <Text style={styles.summaryScore}>
                {winner.score.toLocaleString()} vs {loser.score.toLocaleString()} {unit}
              </Text>
            </View>
            <View style={styles.summaryLine} />
          </View>

          {/* Record update */}
          {record && (
            <View style={styles.recordBlock}>
              <Text style={styles.recordLabel}>RECORD</Text>
              <Text style={styles.recordValue}>{record}</Text>
            </View>
          )}

          {/* CTA */}
          <View style={styles.ctaBlock}>
            <View style={styles.ctaLine} />
            <Text style={styles.ctaMain}>DO U EVEN LIFT?</Text>
            <Text style={styles.ctaSub}>Challenge me on DUEL</Text>
            <View style={styles.ctaLine} />
          </View>

          {/* Footer */}
          <View style={styles.footer}>
            <Text style={styles.footerUrl}>DOUEVENLIFT.COM</Text>
            <Text style={styles.footerTag}>Competitive Strength Training</Text>
          </View>
        </View>

        {showWatermark && (
          <View style={styles.watermark} pointerEvents="none">
            <Text style={styles.watermarkText}>duel.app</Text>
          </View>
        )}
      </View>
    );
  },
);

DuelShareCard.displayName = 'DuelShareCard';
export default DuelShareCard;

const styles = StyleSheet.create({
  card: {
    width: 1080,
    minHeight: 1080,
    backgroundColor: '#030303',
    position: 'relative',
  },
  cardStory: {
    height: 1920,
    minHeight: 1920,
    justifyContent: 'center',
  },

  topGlow: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 350,
  },

  accentBar: {
    height: 8,
    backgroundColor: '#B8972E',
  },

  content: {
    padding: 72,
    paddingTop: 56,
    alignItems: 'center',
    flex: 1,
  },

  brandBlock: {
    alignItems: 'center',
    marginBottom: 32,
  },
  brandText: {
    color: '#E8E4DB',
    fontSize: 72,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 16,
  },
  brandLine: {
    width: 120,
    height: 3,
    backgroundColor: '#B8972E',
    marginTop: 12,
  },

  headerLabel: {
    color: '#B8972E',
    fontSize: 64,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 8,
    marginBottom: 8,
  },
  date: {
    color: '#4A4840',
    fontSize: 24,
    fontFamily: 'Inter_400Regular',
    marginBottom: 40,
  },

  exerciseBlock: {
    alignItems: 'center',
    marginBottom: 48,
  },
  exercise: {
    color: '#E8E4DB',
    fontSize: 56,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 3,
    textAlign: 'center',
  },
  meta: {
    color: '#7A776E',
    fontSize: 28,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 3,
    marginTop: 12,
  },

  battleSection: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 48,
    width: '100%',
    position: 'relative',
  },
  playerSide: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: '#0A0A0A',
    borderWidth: 2,
    borderRadius: 2,
    padding: 36,
    gap: 12,
  },
  winnerSide: {
    borderColor: '#B8972E',
    backgroundColor: '#0F0D08',
  },
  loserSide: {
    borderColor: '#1A1A1A',
    backgroundColor: '#0A0A0A',
  },

  vsContainer: {
    position: 'absolute',
    left: '50%',
    marginLeft: -44,
    zIndex: 10,
  },
  vsBadge: {
    backgroundColor: '#B8972E',
    width: 88,
    height: 88,
    borderRadius: 2,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 4,
    borderColor: '#030303',
  },
  vsText: {
    color: '#030303',
    fontSize: 32,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 2,
  },

  winnerLabel: {
    color: '#B8972E',
    fontSize: 22,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 4,
  },
  loserLabel: {
    color: '#4A4840',
    fontSize: 22,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 4,
  },
  avatar: {
    width: 100,
    height: 100,
    borderRadius: 2,
  },
  avatarPlaceholder: {
    width: 100,
    height: 100,
    borderRadius: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  winnerAvatar: {
    backgroundColor: '#B8972E',
  },
  loserAvatar: {
    backgroundColor: '#1A1A1A',
  },
  avatarLetter: {
    color: '#030303',
    fontSize: 48,
    fontFamily: 'Oswald_700Bold',
  },
  playerName: {
    color: '#E8E4DB',
    fontSize: 30,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 1,
  },
  score: {
    fontSize: 60,
    fontFamily: 'Oswald_700Bold',
  },
  winnerScore: {
    color: '#B8972E',
  },
  loserScore: {
    color: '#7A776E',
  },
  scoreUnit: {
    color: '#4A4840',
    fontSize: 22,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 2,
    marginTop: -12,
  },

  summaryBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 32,
    width: '100%',
  },
  summaryLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#1A1A1A',
  },
  summaryCenter: {
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  summaryText: {
    color: '#E8E4DB',
    fontSize: 26,
    fontFamily: 'Inter_600SemiBold',
    textAlign: 'center',
  },
  summaryScore: {
    color: '#7A776E',
    fontSize: 22,
    fontFamily: 'Inter_400Regular',
    textAlign: 'center',
    marginTop: 8,
  },

  recordBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    marginBottom: 32,
  },
  recordLabel: {
    color: '#4A4840',
    fontSize: 22,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 4,
  },
  recordValue: {
    color: '#E8E4DB',
    fontSize: 32,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 2,
  },

  ctaBlock: {
    alignItems: 'center',
    gap: 16,
    marginBottom: 40,
  },
  ctaLine: {
    width: 200,
    height: 2,
    backgroundColor: '#B8972E',
  },
  ctaMain: {
    color: '#B8972E',
    fontSize: 36,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 4,
    textAlign: 'center',
  },
  ctaSub: {
    color: '#7A776E',
    fontSize: 26,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 3,
  },

  footer: {
    alignItems: 'center',
  },
  footerUrl: {
    color: '#E8E4DB',
    fontSize: 28,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 8,
  },
  footerTag: {
    color: '#4A4840',
    fontSize: 20,
    fontFamily: 'BarlowCondensed_700Bold',
    letterSpacing: 3,
    marginTop: 6,
  },
  watermark: {
    position: 'absolute',
    bottom: 32,
    right: 32,
    backgroundColor: 'rgba(0,0,0,0.55)',
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 2,
  },
  watermarkText: {
    color: 'rgba(232,228,219,0.7)',
    fontSize: 36,
    fontFamily: 'Oswald_700Bold',
    letterSpacing: 4,
    textTransform: 'uppercase' as const,
  },
});
